import './bootstrap';

// FontAwesome restored here per preference (single entry point for icon management)
import { library, dom, config } from '@fortawesome/fontawesome-svg-core'
import '@fortawesome/fontawesome-svg-core/styles.css'
config.autoAddCss = false
config.observeMutations = false

import {
  faArrowLeft, faArrowRight, faAward, faBrush, faCalendar,
  faChevronLeft, faChevronRight, faEnvelope, faEye, faEyeSlash,
  faFileLines, faHeadset, faHeart, faHelmetSafety, faMedal,
  faPhone, faCartShopping, faStamp, faArrowsRotate, faTicket
} from '@fortawesome/free-solid-svg-icons'
import { faHeart as faHeartRegular, faFileLines as faFileLinesRegular } from '@fortawesome/free-regular-svg-icons'

library.add(
  faArrowLeft, faArrowRight, faAward, faBrush, faCalendar,
  faChevronLeft, faChevronRight, faEnvelope, faEye, faEyeSlash,
  faFileLines, faHeadset, faHeart, faHelmetSafety, faMedal,
  faPhone, faCartShopping, faStamp, faArrowsRotate, faTicket,
  faHeartRegular, faFileLinesRegular
)

document.addEventListener('DOMContentLoaded', () => {
  const run = () => dom.i2svg({ node: document.body });
  if ('requestIdleCallback' in window) requestIdleCallback(run);
  else setTimeout(run, 0); // defer out of critical path
});

// Swiper core + CSS (include module styles for navigation & pagination)
import Swiper from 'swiper'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import { Navigation, Pagination, Autoplay } from 'swiper/modules'

// Expose constructor globally for inline light/late initializers (e.g., testimonials partial)
window.Swiper = Swiper;
window.SwiperModules = { Navigation, Pagination, Autoplay };

// Generic data-* driven initializer (elements can opt‑in via data-swiper-config JSON)
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-swiper-config]')?.forEach(el => {
    try {
      const raw = el.getAttribute('data-swiper-config') || '{}';
      const cfg = JSON.parse(raw);
      const modules = [];
      if (cfg.useNavigation) modules.push(Navigation);
      if (cfg.usePagination) modules.push(Pagination);
      if (cfg.useAutoplay) modules.push(Autoplay);
      new Swiper(el, { ...cfg.options, modules });
    } catch (e) {
      console && console.warn && console.warn('Failed to init Swiper on', el, e);
    }
  });
});

// Legacy fallback removed: we now rely exclusively on data-swiper attributes.
// If any old markup without data-swiper needs init, add data-swiper and optional data-* attributes.

// Data-attribute driven initializer for elements with [data-swiper]
// Supports:
//  data-slides="<int>" (default slidesPerView)
//  data-space="<int>" (spaceBetween)
//  data-breakpoints='{"1200":{"slidesPerView":3},"1600":{"slidesPerView":4}}'
//  Adds navigation & pagination if matching child elements exist.
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-swiper]')?.forEach(el => {
    if (el.__swiper) return; // already initialized
    const parseIntAttr = (name, def = 0) => {
      const v = el.getAttribute(name);
      return v === null ? def : (parseInt(v, 10) || def);
    };
    const parseBoolAttr = (name, def = false) => {
      const v = el.getAttribute(name);
      if (v === null) return def;
      const s = String(v).toLowerCase();
      return s === '1' || s === 'true' || s === '' ? true : (s === '0' || s === 'false' ? false : def);
    };
    const slides = parseIntAttr('data-slides', 1);
    const space = parseIntAttr('data-space', 0);
    const speed = parseIntAttr('data-speed', 300);
    const loop = parseBoolAttr('data-loop', false);
    const autoplayEnabled = parseBoolAttr('data-autoplay', false);
    const autoplayDelay = parseIntAttr('data-delay', 3000);
    const disableOnInteraction = parseBoolAttr('data-disable-on-interaction', true);
    let breakpoints = {};
    const bpRaw = el.getAttribute('data-breakpoints');
    if (bpRaw) {
      try { breakpoints = JSON.parse(bpRaw); } catch (e) { console.warn('Invalid data-breakpoints JSON', e); }
    }
    const navigation = {
      nextEl: el.querySelector('.swiper-button-next'),
      prevEl: el.querySelector('.swiper-button-prev')
    };
    const paginationEl = el.querySelector('.swiper-pagination');
    const pagination = paginationEl ? { el: paginationEl, clickable: true } : undefined;
    try {
      const modules = [Navigation, Pagination];
      const options = {
        slidesPerView: slides,
        spaceBetween: space,
        speed,
        loop,
        breakpoints,
        navigation,
        pagination
      };
      if (autoplayEnabled) {
        modules.push(Autoplay);
        options.autoplay = { delay: autoplayDelay, disableOnInteraction };
      }
      options.modules = modules;
      el.__swiper = new Swiper(el, options);
    } catch (e) { console.warn('Failed to init swiper', e); }
  });
});

// Delegated password visibility toggle (robust against FontAwesome i2svg replacement)
// Ensure password toggle icons show pointer cursor & sit above input
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.password-toggle-icon,[data-toggle-password]')
    .forEach(el => { el.style.cursor = 'pointer'; el.style.zIndex = '3'; });
});

// Delegated handler: works for elements having either class or data attribute.
// Prevents input from stealing focus on click when user intends to toggle visibility.
// Delegated handler: works for elements having either class or data attribute.
// Prevents input from stealing focus on click when user intends to toggle visibility.
document.addEventListener('click', (e) => {
  const toggleEl = e.target.closest('.password-toggle-icon,[data-toggle-password],.password-toggle-wrapper');
  if (!toggleEl) return;
  e.preventDefault();
  e.stopPropagation();

  const inputId = toggleEl.getAttribute('data-toggle-password');
  // If wrapper clicked, seek inner icon for visual swap
  // We look for .password-toggle-icon OR standard FA classes as fallback
  let iconNode = toggleEl.classList.contains('password-toggle-icon') ? toggleEl : toggleEl.querySelector('.password-toggle-icon, .fa-eye, .fa-eye-slash');

  // Resolve input field
  const field = inputId ? document.getElementById(inputId) : toggleEl.parentElement?.querySelector('input[type="password"], input[data-type="password"], input[data-original-type="password"]');

  if (!field) return;

  const show = field.type === 'password';
  field.type = show ? 'text' : 'password';

  if (iconNode) {
    if (show) {
      // Was password (hidden), now text (visible) -> Show 'eye' (open)
      // Remove 'slash' (hidden state indicator)
      iconNode.classList.remove('fa-eye-slash');
      iconNode.classList.add('fa-eye');
    } else {
      // Was text (visible), now password (hidden) -> Show 'eye-slash'
      iconNode.classList.remove('fa-eye');
      iconNode.classList.add('fa-eye-slash');
    }
  }
});